import { Center, Loader } from "@mantine/core";
import type { RefetchOptions } from "@tanstack/react-query";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import type { GeneratorChangesFromMastr } from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { DATE_DISPLAY_FORMAT } from "../../../../utils/dates/dates.constants";
import { BasicConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";

const columns: Array<MRT_ColumnDef<GeneratorChangesFromMastr>> = [
  { accessorKey: "fieldVerboseName", header: "Feld" },
  {
    accessorKey: "oldValue",
    header: "Alter Wert",
    Cell: ({ row }) =>
      formatValue(row.original.fieldName, row.original.oldValue)
  },
  {
    accessorKey: "newValue",
    header: "Neuer Wert",
    Cell: ({ row }) =>
      formatValue(row.original.fieldName, row.original.newValue)
  }
];

interface GeneratorOverwriteConfirmationModalProps {
  possibleChanges?: Array<GeneratorChangesFromMastr> | Array<never>;
  isGettingPossibleChanges: boolean;
  isOverwritingData: boolean;
  isGeneratorOverwriteConfirmationModalOpen: boolean;
  refetchPossibleChanges: (options?: RefetchOptions) => void;
  onOverwriteGeneratorData: () => void;
  onCloseGeneratorOverwriteConfirmationModal: () => void;
}

function GeneratorOverwriteConfirmationModal({
  possibleChanges,
  isGettingPossibleChanges,
  isOverwritingData,
  isGeneratorOverwriteConfirmationModalOpen,
  refetchPossibleChanges,
  onOverwriteGeneratorData,
  onCloseGeneratorOverwriteConfirmationModal
}: GeneratorOverwriteConfirmationModalProps) {
  useEffect(() => {
    refetchPossibleChanges();
  }, [refetchPossibleChanges]);

  return (
    <BasicConfirmationModal
      cancelButtonText="Abbrechen"
      confirmationButtonColor="warning"
      confirmationButtonText="Fortfahren"
      isConfirmationBtnDisabled={isGettingPossibleChanges}
      isLoading={isOverwritingData}
      opened={isGeneratorOverwriteConfirmationModalOpen}
      size="xl"
      title="Daten des Erzeugers werden überschrieben"
      toggleModal={onCloseGeneratorOverwriteConfirmationModal}
      onCancel={onCloseGeneratorOverwriteConfirmationModal}
      onConfirm={onOverwriteGeneratorData}
    >
      <ModalBody
        isGettingPossibleChanges={isGettingPossibleChanges}
        possibleChanges={possibleChanges}
      />
    </BasicConfirmationModal>
  );
}

interface ModalBodyProps {
  possibleChanges?: Array<GeneratorChangesFromMastr>;
  isGettingPossibleChanges: boolean;
}

function ModalBody({
  possibleChanges,
  isGettingPossibleChanges
}: ModalBodyProps) {
  const table = useMantineReactTable<GeneratorChangesFromMastr>({
    ...getDefaultMRTOptions({ hideBorder: true }),
    enableSorting: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    columns: columns,
    data: possibleChanges || []
  });

  if (isGettingPossibleChanges) {
    return (
      <div>
        <p>
          Die neuesten Daten werden aus dem Marktstammdatenregister abgerufen.
        </p>
        <Center>
          <Loader />
        </Center>
      </div>
    );
  }

  return (
    <div>
      {possibleChanges && possibleChanges.length > 0 ? (
        <>
          <p>
            Alle Daten des Erzeugers in opti.node werden automatisch durch die
            entsprechenden Daten der Anlage im Marktstammdatenregister
            überschrieben.
          </p>
          <p>Wollen Sie fortfahren?</p>
        </>
      ) : (
        <p>
          Die Daten auf dem Erzeuger stimmen mit den Daten im
          Marktstammdatenregister überein. Eine Aktualisierung ist nicht
          notwendig.
        </p>
      )}

      {possibleChanges && possibleChanges.length > 0 && (
        <MantineReactTable table={table} />
      )}
    </div>
  );
}

function formatValue(fieldName: string, value: string | null): string {
  if (!value) {
    return "—";
  }

  if (fieldName === "registrierungsdatum") {
    return backendDateOrDateTimeToLuxonDateTime(value).toFormat(
      DATE_DISPLAY_FORMAT
    );
  }

  return value;
}

export {
  GeneratorOverwriteConfirmationModal,
  GeneratorOverwriteConfirmationModalProps
};
